import { Assert } from '@cotera/utilities';
import type { DatasetOperationV20260601 } from '@cotera/api';
import type { DuckDbCellValue } from '@cotera/client/app/etc/duckdb';
import type { DatasetPreviewColumn } from './operations';

/**
 * What one operation does to rows the grid is already showing.
 *
 * The companion to `datasetOperationEffect`, which says what an operation does
 * to the DuckDB table. Both are needed and neither implies the other: the table
 * is what sorting, filtering and header stats read, and the loaded rows are what
 * is on screen. Writing only the table leaves the screen stale until something
 * re-queries it, which is the bug this exists to close.
 *
 * Deliberately a separate exhaustive switch rather than another field on
 * `DatasetOperationEffect`. The two answers have different *shapes* — a schema
 * change is a handful of statements and no patch at all — and the effect is
 * also used by the from-scratch replay, which has no grid to patch.
 */
export type DatasetOperationPatch =
  | {
      readonly type: 'update-cell';
      readonly rowId: string;
      readonly columnName: string;
      readonly value: DuckDbCellValue;
    }
  | {
      readonly type: 'insert-row';
      readonly row: Record<string, DuckDbCellValue>;
    }
  | { readonly type: 'delete-row'; readonly rowId: string };

export type DatasetOperationPatchPlan = {
  /** Applied in order to the rows the grid holds. */
  readonly patches: readonly DatasetOperationPatch[];
  /**
   * True when at least one operation changed the schema.
   *
   * A column appearing, being renamed or going away is not a patch: the column
   * list is what the adapter, the layer stack and the whole grid view model are
   * built from, so the only honest response is to rebuild them. It is also the
   * rare case — the common one, by a wide margin, is a workflow writing cells.
   */
  readonly needsRebuild: boolean;
};

/**
 * Turns a batch of operations into the changes the loaded rows need.
 *
 * `columns` is the schema the grid is showing, and an inserted row is filled out
 * against it: the operation carries only the values something chose to write, so
 * every other column has to be explicitly null or the row arrives missing cells
 * that a re-query would have returned as empty.
 *
 * Returns no patches at all once `needsRebuild` is set. Half-patching a batch
 * whose tail rebuilds everything would paint values into a grid that is about to
 * be replaced, and — for an insert — paint them twice.
 */
export const datasetOperationPatches = (
  operations: readonly DatasetOperationV20260601[],
  {
    columns,
    rowIdColumn,
  }: {
    readonly columns: readonly DatasetPreviewColumn[];
    /**
     * Column rows are addressed by. Null for legacy artifacts with no durable
     * row id, which leaves nothing to patch: the grid falls back to a row's
     * position in the current page, and no operation names one.
     */
    readonly rowIdColumn: string | null;
  }
): DatasetOperationPatchPlan => {
  if (rowIdColumn === null) {
    return { patches: [], needsRebuild: false };
  }
  const patches: DatasetOperationPatch[] = [];
  for (const operation of operations) {
    switch (operation.type) {
      case 'update_cell':
        patches.push({
          type: 'update-cell',
          rowId: operation.rowId,
          columnName: operation.columnName,
          value: operation.value,
        });
        break;
      case 'insert_row': {
        // A row the server did not name cannot be addressed afterwards — an
        // edit to it, or a later operation on it, would find nothing. The
        // rebuild path invents an id because it is replaying into a table
        // nobody has looked at yet; here the id has to be the real one.
        if (operation.rowId === undefined) {
          return { patches: [], needsRebuild: true };
        }
        patches.push({
          type: 'insert-row',
          row: {
            ...Object.fromEntries(columns.map((column) => [column.name, null])),
            ...operation.values,
            [rowIdColumn]: operation.rowId,
          },
        });
        break;
      }
      case 'delete_row':
        patches.push({ type: 'delete-row', rowId: operation.rowId });
        break;
      // Every remaining variant changes the schema, and the schema is what the
      // grid is built from rather than something it holds.
      case 'add_column':
      case 'rename_column':
      case 'delete_column':
        return { patches: [], needsRebuild: true };
      default:
        return Assert.unreachable(operation);
    }
  }
  return { patches, needsRebuild: false };
};
